import React from 'react';
import { usePermissions } from '../contexts/PermissionContext';
import { Result, Button } from 'antd';
import { LockOutlined } from '@ant-design/icons';

/**
 * PermissionGate - Conditionally renders children based on user permissions.
 *
 * Usage:
 *
 * // Hide content if no permission (default behavior)
 * <PermissionGate resource="contracts" action="edit">
 *   <Button>Edit Contract</Button>
 * </PermissionGate>
 *
 * // Show fallback content if no permission
 * <PermissionGate resource="users" action="delete" fallback={<span>No access</span>}>
 *   <Button danger>Delete User</Button>
 * </PermissionGate>
 *
 * // Require any of multiple permissions
 * <PermissionGate any={[['contracts', 'edit'], ['contracts', 'delete']]}>
 *   <Button>Manage Contract</Button>
 * </PermissionGate>
 *
 * // Require all of multiple permissions
 * <PermissionGate all={[['users', 'view'], ['users', 'edit']]}>
 *   <Button>Edit User</Button>
 * </PermissionGate>
 *
 * // Require specific role
 * <PermissionGate role="admin">
 *   <AdminPanel />
 * </PermissionGate>
 *
 * // Require any of specified roles
 * <PermissionGate roles={['admin', 'manager']}>
 *   <ApprovalButtons />
 * </PermissionGate>
 */
const PermissionGate = ({
  children,
  resource,
  action,
  any,
  all,
  role,
  roles,
  fallback = null,
  showDenied = false,
}) => {
  const { can, canAny, canAll, hasRole, hasAnyRole, loading } = usePermissions();

  // Still loading permissions
  if (loading) {
    return null;
  }

  let hasPermission = false;

  // Check single resource/action
  if (resource && action) {
    hasPermission = can(resource, action);
  }
  // Check any of multiple permissions
  else if (any) {
    hasPermission = canAny(any);
  }
  // Check all of multiple permissions
  else if (all) {
    hasPermission = canAll(all);
  }
  // Check single role
  else if (role) {
    hasPermission = hasRole(role);
  }
  // Check any of multiple roles
  else if (roles) {
    hasPermission = hasAnyRole(roles);
  }
  // No permission check specified - show children
  else {
    hasPermission = true;
  }

  if (hasPermission) {
    return children;
  }

  // Show access denied page if requested
  if (showDenied) {
    return (
      <Result
        status="403"
        title="Access Denied"
        subTitle="You don't have permission to access this content."
        icon={<LockOutlined />}
        extra={
          <Button type="primary" onClick={() => window.history.back()}>
            Go Back
          </Button>
        }
      />
    );
  }

  // Return fallback content or nothing
  return fallback;
};

/**
 * withPermission - HOC for permission-based component rendering.
 *
 * Usage:
 * const ProtectedButton = withPermission(Button, 'contracts', 'edit');
 * <ProtectedButton>Edit Contract</ProtectedButton>
 */
export const withPermission = (Component, resource, action) => {
  return (props) => (
    <PermissionGate resource={resource} action={action}>
      <Component {...props} />
    </PermissionGate>
  );
};

/**
 * ProtectedRoute - Wrapper for routes that require permissions.
 *
 * Usage in App.js routes:
 * <Route path="/admin" element={
 *   <ProtectedRoute resource="users" action="view">
 *     <AdminPage />
 *   </ProtectedRoute>
 * } />
 */
export const ProtectedRoute = ({ children, ...props }) => {
  return (
    <PermissionGate {...props} showDenied={true}>
      {children}
    </PermissionGate>
  );
};

export default PermissionGate;
